import { NetworkError } from './errors';

export interface TwitchUser {
  id: string;
  login: string;
  display_name: string;
  profile_image_url: string;
  email?: string;
}

export interface TwitchTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  scope: string[];
  token_type: string;
}

class TwitchService {
  private static instance: TwitchService;
  private clientId: string;
  private redirectUri: string;
  private accessToken: string | null = null;
  private user: TwitchUser | null = null;
  private apiBaseUrl: string;

  private constructor() {
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    this.clientId = import.meta.env.VITE_TWITCH_CLIENT_ID || '';
    this.redirectUri = `${baseUrl}/auth/callback`;
    this.apiBaseUrl = `${baseUrl}/api`;

    if (!this.clientId) {
      console.error('VITE_TWITCH_CLIENT_ID is not set in environment variables');
    }

    this.loadStoredAuth();
  }

  public static getInstance(): TwitchService {
    if (!TwitchService.instance) {
      TwitchService.instance = new TwitchService();
    }
    return TwitchService.instance;
  }

  public initiateLogin(): void {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: this.requiredScopes.join(' '),
      force_verify: 'true',
      force_login: 'true'
    });

    window.location.href = `https://id.twitch.tv/oauth2/authorize?${params}`;
  }

  public async handleCallback(code: string): Promise<void> {
    if (!code) {
      throw new Error('auth_required: No authorization code provided');
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/auth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code,
          redirect_uri: this.redirectUri
        })
      });

      if (!response.ok) {
        throw new Error('auth_required: Failed to get access token');
      }

      const data: TwitchTokenResponse = await response.json();
      
      if (!data.access_token || !data.refresh_token) {
        throw new Error('auth_required: Invalid token response');
      }

      this.accessToken = data.access_token;
      const expiryTime = Date.now() + (data.expires_in * 1000);
      
      localStorage.setItem('twitch_access_token', data.access_token);
      localStorage.setItem('twitch_refresh_token', data.refresh_token);
      localStorage.setItem('twitch_token_expiry', expiryTime.toString());

      const user = await this.fetchUserInfo();
      if (!user) {
        throw new Error('auth_required: Failed to fetch user info after authentication');
      }
      
      localStorage.setItem('twitch_user', JSON.stringify(user));
    } catch (error) {
      console.error('Auth error:', error);
      throw error;
    }
  }

  private readonly requiredScopes = [
    'openid',
    'user_read',
    'channel:manage:raids',
    'user:read:follows',
    'moderator:read:chatters',
    'moderator:read:followers',
    'moderation:read',
    'channel:read:vips',
    'moderator:manage:banned_users',
    'moderator:manage:shoutouts',
    'user:read:chat',
    'user:write:chat',
    'channel:read:subscriptions',
    'moderator:manage:announcements',
    'user:read:follows'
  ];
  
  public async validateScopes(): Promise<boolean> {
    try {
      console.log(`VALIDATING TOKEN: ${this.accessToken}`)
      const response = await fetch('https://id.twitch.tv/oauth2/validate', {
        headers: {
          'Authorization': `OAuth ${this.accessToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 400) {
          console.log("Token validation failed, refreshing page");
          window.location.reload();
          return false;
        }
        console.log('Network error in validateScopes, refreshing page')
        window.location.reload();
        throw new NetworkError('network_error: Network error or server error occurred');
      }

      const data = await response.json();
      const currentScopes = data.scopes as string[];
      const hasAllScopes = this.requiredScopes.every(scope => currentScopes.includes(scope));
      
      if (!hasAllScopes) {
        console.log("Missing required scopes, refreshing page");
        window.location.reload();
      }
      
      return hasAllScopes;
    } catch (error) {
      console.error('Validation error:', error);
      window.location.reload();
      return false;
    }
  }

  private clearStoredAuth(): void {
    this.accessToken = null;
    this.user = null;
    localStorage.removeItem('twitch_access_token');
    localStorage.removeItem('twitch_refresh_token');
    localStorage.removeItem('twitch_token_expiry');
    localStorage.removeItem('twitch_user');
  }

  /** Refresh the access token only when it is close to expiring. */
  public async ensureAccessTokenFresh(): Promise<boolean> {
    return this.checkAndRefreshToken();
  }

  private loadStoredAuth(): void {
    try {
      this.accessToken = localStorage.getItem('twitch_access_token');
      const storedUser = localStorage.getItem('twitch_user');
      if (storedUser) {
        this.user = JSON.parse(storedUser);
      }
    } catch (error) {
      console.error('Failed to load stored auth:', error);
    }
  }

  private async checkAndRefreshToken(): Promise<boolean> {
    const expiry = localStorage.getItem('twitch_token_expiry');
    if (!expiry) return false;

    const expiryTime = parseInt(expiry);
    const now = Date.now();

    // Refresh token if it's within 30 minutes of expiring
    const REFRESH_BUFFER = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    if (now >= expiryTime - REFRESH_BUFFER) {
      console.log('Token approaching expiry, refreshing proactively');
      return await this.refreshToken();
    }
    return true;
  }

  private async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    if (!this.accessToken) {
      console.log("No access token available, refreshing page");
      window.location.reload();
      throw new Error('auth_required: No access token available');
    }

    try {
      const isValid = await this.checkAndRefreshToken();
      
      if (!isValid) {
        throw new Error('auth_required: Token refresh failed');
      }

      const headers = {
        'Authorization': `Bearer ${this.accessToken}`,
        'Client-Id': this.clientId,
        ...options.headers
      };

      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        console.log(`Request failed with status ${response.status}, refreshing page`);
        window.location.reload();
        throw new Error(`Request failed with status ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Request error:', error);
      window.location.reload();
      throw error;
    }
  }

  public async fetchUserInfo(): Promise<TwitchUser | null> {
    try {
      const response = await this.makeAuthenticatedRequest('https://api.twitch.tv/helix/users');
      const data = await response.json();
      if (!data.data?.[0]) {
        console.log("No user data received, refreshing page");
        window.location.reload();
        throw new Error('auth_required: No user data received');
      }

      this.user = data.data[0];
      return this.user;
    } catch (error) {
      console.error('Error fetching user info:', error);
      if ((error as Error).message.includes('auth_required')) {
        window.location.reload();
      }
      return null;
    }
  }

  public async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('twitch_refresh_token');
    
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        console.log(`Token refresh failed with status ${response.status}`);
        if (response.status === 401 || response.status === 400) {
          this.clearStoredAuth();
        }
        return false;
      }

      const data: TwitchTokenResponse = await response.json();
      
      if (!data.access_token || !data.refresh_token) {
        console.log("Invalid token response from refresh");
        return false;
      }

      this.accessToken = data.access_token;
      localStorage.setItem('twitch_access_token', data.access_token);
      localStorage.setItem('twitch_refresh_token', data.refresh_token);
      localStorage.setItem('twitch_token_expiry', (Date.now() + data.expires_in * 1000).toString());
      
      // Don't validate scopes here - let the caller handle that
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  public async isAuthenticated(): Promise<boolean> {
    const token = localStorage.getItem('twitch_access_token');
    const refreshToken = localStorage.getItem('twitch_refresh_token');
    const storedUser = localStorage.getItem('twitch_user');
    
    if (!token || !refreshToken || !storedUser) {
      return false;
    }

    const isAuthed = await this.checkAndRefreshToken();
    if (!isAuthed) {
      return false;
    }

    const user = JSON.parse(storedUser);
    if (!user.id || !user.login) {
      return false;
    }

    return true;
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public getUser(): TwitchUser | null {
    if (this.user) {
      return this.user;
    }

    const storedUser = localStorage.getItem('twitch_user');
    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser);
        return this.user;
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        return null;
      }
    }

    return null;
  }

  public getChannelName(): string | null {
    const user = this.getUser();
    return user ? user.login : null;
  }

  public logout(): void {
    this.clearStoredAuth();

    const currentUrl = window.location.href;
    const loginUrl = `${window.location.protocol}//${window.location.host}/login`;

    if (currentUrl !== loginUrl) {
        window.location.href = '/login';
    }

  }
}

export const twitchService = TwitchService.getInstance();
